/**
 * ProofOfDelivery - Displays delivery confirmation data for an order.
 * Only renders when `order.delivery_confirmation` exists.
 *
 * Shows: received by, relationship, timestamps, GPS pin, driver, notes,
 *        signature image, photo gallery, and video player.
 * Media files are fetched via authenticated requests to /api/delivery-media/.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { API } from '@/config/constants';
import useReceiptLinkCopy from '@/hooks/useReceiptLinkCopy';
import {
  MapPin, Clock, User, PenLine, Camera, Video,
  FileText, ExternalLink, ChevronDown, ChevronUp, X, Link2
} from 'lucide-react';
import { useAuthenticatedMedia } from '@/hooks/useAuthenticatedMedia';
import { offlineMediaLabel } from '@/utils/mediaPresentation';

// Format an ISO timestamp for display
const fmtTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return iso;
  }
};

const ProofOfDelivery = ({ order, readOnly = false, copyEnabled = true, media: suppliedMedia, onRetryMedia, onMediaError }) => {
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const confirmation = order?.delivery_confirmation;
  const receiptCopy = useReceiptLinkCopy(order?.id, !!confirmation && !readOnly && copyEnabled);

  const mediaFiles = useMemo(() => confirmation?.media_files || [], [confirmation?.media_files]);
  const signature = mediaFiles.find((f) => f.file_type === 'signature');
  const photos = mediaFiles.filter((f) => f.file_type === 'photo');
  const videoFile = mediaFiles.find((f) => f.file_type === 'video');
  const gps = confirmation?.gps_location;

  const mediaRequests = useMemo(() => suppliedMedia ? [] : mediaFiles.flatMap((file) => file.storage_path ? [{
    key: file.storage_path,
    url: `${API}/delivery-media/${encodeURIComponent(file.storage_path)}`,
  }] : []), [mediaFiles, suppliedMedia]);
  const defaultMedia = useAuthenticatedMedia(mediaRequests);
  const loadedMedia = suppliedMedia || defaultMedia;
  const mediaUrls = Object.fromEntries(
    Object.entries(loadedMedia).map(([path, result]) => [path, result.url]),
  );
  const loadingMedia = Object.values(loadedMedia).some((result) => result.state === 'loading');
  const isLoadingMedia = (path) => readOnly ? loadedMedia[path]?.state === 'loading' : loadingMedia;

  useEffect(() => setLightboxSrc(null), [order?.id]);
  useEffect(() => {
    if (lightboxSrc && !Object.values(loadedMedia).some((item) => item.url === lightboxSrc)) setLightboxSrc(null);
  }, [lightboxSrc, loadedMedia]);

  const mediaFailure = (path) => {
    const result = loadedMedia[path];
    if (!readOnly) return offlineMediaLabel(result);
    const label = result?.state === 'forbidden' ? 'Access to this media was refused.'
      : result?.state === 'error' ? 'This media could not be loaded.' : 'This media is unavailable.';
    return <span>{label}{onRetryMedia && <button type="button" className="block underline mt-1" onClick={() => onRetryMedia(path)}>Retry media</button>}</span>;
  };

  if (!confirmation) return null;

  const gpsLink =
    (readOnly ? Number.isFinite(gps?.latitude) && Number.isFinite(gps?.longitude) : gps?.latitude && gps?.longitude)
      && !(gps.latitude === 0 && gps.longitude === 0)
      ? `https://www.google.com/maps?q=${gps.latitude},${gps.longitude}`
      : null;

  return (
    <div
      className="md:col-span-2 mt-2 rounded-xl border border-green-200 bg-green-50/60 overflow-hidden"
      data-testid={`pod-section-${order.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-green-100/80 border-b border-green-200">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-green-700" />
          <span className="text-sm font-semibold text-green-900">Proof of Delivery</span>
        </div>
        {!readOnly && <button
          onClick={(e) => {
            e.stopPropagation();
            void receiptCopy.copy();
          }}
          disabled={!copyEnabled || receiptCopy.pending}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-green-700 hover:bg-green-200/60 transition-colors"
          data-testid={`pod-copy-link-${order.id}`}
          title="Copy shareable receipt link"
        >
          <Link2 className="h-3.5 w-3.5" />
          {receiptCopy.waiting ? 'Still waiting…' : receiptCopy.pending ? 'Copying…' : 'Copy Link'}
        </button>}
      </div>

      <div className="p-4 space-y-4 text-sm">
        {/* Key fields in a 2-col grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {/* Received by */}
          <div className="flex items-start gap-2">
            <User className="h-4 w-4 text-green-700 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-stone-500 text-xs">Received by</p>
              <p className="font-medium text-stone-900" data-testid={`pod-received-by-${order.id}`}>
                {confirmation.received_by}
                {confirmation.received_by_relationship && (
                  <span className="text-stone-500 font-normal"> ({confirmation.received_by_relationship})</span>
                )}
              </p>
            </div>
          </div>

          {/* Driver, or manual back-entry attribution */}
          <div className="flex items-start gap-2">
            <PenLine className="h-4 w-4 text-green-700 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-stone-500 text-xs">{confirmation.manual_entry ? 'Recorded by' : 'Confirmed by'}</p>
              <p className="font-medium text-stone-900" data-testid={`pod-driver-${order.id}`}>
                {confirmation.manual_entry
                  ? `Manually recorded by ${confirmation.manually_recorded_by || 'admin'}`
                  : (confirmation.confirmed_by_user_name || 'Driver')}
              </p>
            </div>
          </div>

          {/* Delivery time */}
          <div className="flex items-start gap-2">
            <Clock className="h-4 w-4 text-green-700 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-stone-500 text-xs">Delivered at</p>
              <p className="font-medium text-stone-900" data-testid={`pod-time-delivered-${order.id}`}>
                {fmtTime(confirmation.time_delivered || confirmation.confirmed_at) || 'N/A'}
              </p>
            </div>
          </div>

          {/* GPS */}
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-green-700 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-stone-500 text-xs">GPS Location</p>
              {gpsLink ? (
                <a
                  href={gpsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-green-700 hover:text-green-900 hover:underline inline-flex items-center gap-1"
                  data-testid={`pod-gps-link-${order.id}`}
                >
                  {gps.latitude.toFixed(5)}, {gps.longitude.toFixed(5)}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <p className="text-stone-500">Not captured</p>
              )}
              {gps?.accuracy && (
                <p className="text-xs text-stone-400">±{Math.round(gps.accuracy)}m accuracy</p>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        {confirmation.notes && (
          <div className="rounded-lg bg-white/70 border border-green-100 px-3 py-2">
            <p className="text-xs text-stone-500 mb-0.5">Driver Notes</p>
            <p className="text-stone-800 whitespace-pre-line" data-testid={`pod-notes-${order.id}`}>
              {confirmation.notes}
            </p>
          </div>
        )}

        {/* Signature */}
        {signature && (
          <div>
            <p className="text-xs text-stone-500 mb-1.5">Signature</p>
            {mediaUrls[signature.storage_path] ? (
              <img
                src={mediaUrls[signature.storage_path]}
                alt="Signature"
                className="h-20 rounded-lg border border-stone-200 bg-white cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setLightboxSrc(mediaUrls[signature.storage_path])}
                onError={() => onMediaError?.(signature.storage_path)}
                data-testid={`pod-signature-img-${order.id}`}
              />
            ) : isLoadingMedia(signature.storage_path) ? (
              <div className="h-20 w-40 rounded-lg bg-stone-200 animate-pulse" />
            ) : (
              <p className="text-xs text-stone-400 italic">
                {mediaFailure(signature.storage_path)}
              </p>
            )}
          </div>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <div>
            <p className="text-xs text-stone-500 mb-1.5">Photos ({photos.length})</p>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => {
                const url = mediaUrls[p.storage_path];
                return url ? (
                  <img
                    key={p.storage_path}
                    src={url}
                    alt={`Delivery photo ${i + 1}`}
                    className="h-20 w-20 object-cover rounded-lg border border-stone-200 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setLightboxSrc(url)}
                    onError={() => onMediaError?.(p.storage_path)}
                    data-testid={`pod-photo-${order.id}-${i}`}
                  />
                ) : isLoadingMedia(p.storage_path) ? (
                  <div key={p.storage_path} className="h-20 w-20 rounded-lg bg-stone-200 animate-pulse" />
                ) : (
                  <div key={p.storage_path} className="h-20 w-20 rounded-lg bg-stone-100 p-2 flex items-center justify-center text-center text-[10px] text-stone-500">
                    {mediaFailure(p.storage_path)}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Video */}
        {videoFile && (
          <div>
            <p className="text-xs text-stone-500 mb-1.5">Video</p>
            {mediaUrls[videoFile.storage_path] ? (
              <video
                src={mediaUrls[videoFile.storage_path]}
                controls
                onError={() => onMediaError?.(videoFile.storage_path)}
                className="max-h-48 rounded-lg border border-stone-200"
                data-testid={`pod-video-${order.id}`}
              />
            ) : isLoadingMedia(videoFile.storage_path) ? (
              <div className="h-20 w-40 rounded-lg bg-stone-200 animate-pulse" />
            ) : (
              <p className="text-xs text-stone-400 italic">
                {mediaFailure(videoFile.storage_path)}
              </p>
            )}
          </div>
        )}

        {/* Confirmation timestamp (fine print) */}
        <p className="text-[11px] text-stone-400 pt-1 border-t border-green-100">
          Form submitted {fmtTime(confirmation.time_confirmed || confirmation.confirmed_at)}
        </p>
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxSrc(null)}
          data-testid="pod-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-stone-300"
            onClick={() => setLightboxSrc(null)}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxSrc}
            alt="Full size"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProofOfDelivery;
